/**
 * Image Upload Agent – Dubai Police 999 Emergency Assistant
 *
 * Dedicated agent for evidence collection.
 * Asks the user if they have photos or videos to share.
 *
 * Normal path:  If yes → upload UI, then → agency_agent (consent step)
 * Crisis path:  If isCrisis flag is set → auto-flag all 3 agencies silently,
 *               skip agency_agent entirely, route directly to incident_card_agent.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { flagConversation } from '../utils/flagConversation';

// Reuse the same event from InquiryAgent (imported by AltairAgent)
export const IMAGE_UPLOAD_REQUEST_EVENT = 'dp:requestImageUpload';

const CRISIS_AGENCIES = ['police', 'ambulance', 'civil_defense'];

export function createImageUploadAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'image_upload_agent',
    name: 'Image Upload Agent',
    description: 'Asks about photos/videos for incident documentation, then routes to agency consent (or directly to incident card in crisis)',

    initialPrompt: 'Ask the user in their language if they have photos or videos to share. If yes, call request_image_upload(). Then call proceed_to_agencies(). If no or no response, call proceed_to_agencies() directly.',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_agencies',      { target: { agentId: 'agency_agent' },       guardrails: [languageDetectedGuardrail] }],
        ['to_incident_card', { target: { agentId: 'incident_card_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'agency_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      // ─── request_image_upload ───────────────────────────────────────────
      if (toolName === 'request_image_upload') {
        console.log('[ImageUploadAgent] Image upload requested — emitting UI event');
        window.dispatchEvent(new CustomEvent(IMAGE_UPLOAD_REQUEST_EVENT));
        return {
          toolName, args,
          result: {
            success: true,
            message: 'Image upload UI has been shown to the user. Wait for them to upload images or say they are done/skip. Then call proceed_to_agencies().',
          },
        };
      }

      // ─── proceed_to_agencies / skip_upload ──────────────────────────────
      if (toolName === 'proceed_to_agencies' || toolName === 'skip_upload') {
        const isCrisis = context.transitionData.isCrisis === true;

        // ── Crisis fast-track: auto-flag all agencies, skip consent step ──
        if (isCrisis) {
          console.log(
            '%c[ImageUploadAgent] 🚨 Crisis path — auto-flagging all agencies, routing to incident card',
            'color: #ef4444; font-weight: bold'
          );

          // Silently flag all three agencies and get incident number
          await flagConversation(CRISIS_AGENCIES);

          return {
            toolName, args,
            result: {
              success: true,
              confirmed_agencies: CRISIS_AGENCIES,
              message: 'All emergency services have been notified. Routing to incident card.',
            },
            triggersTransition: true,
            transitionTo: 'incident_card_agent',
            transitionData: {
              confirmedAgencies: CRISIS_AGENCIES,
              incidentSummary: context.transitionData.incidentSummary || '',
              isCrisis: true,
            },
          };
        }

        // ── Normal path: route to agency consent ─────────────────────────
        const recommendedAgencies = context.transitionData.recommendedAgencies || [];
        const confirmedAgencies   = context.transitionData.confirmedAgencies   || [];

        console.log('[ImageUploadAgent] Proceeding to agency consent | agencies:', recommendedAgencies);

        return {
          toolName, args,
          result: { success: true, message: 'Routing to agency consent collection' },
          triggersTransition: true,
          transitionTo: 'agency_agent',
          transitionData: {
            recommendedAgencies,
            confirmedAgencies,
            incidentSummary: context.transitionData.incidentSummary || '',
          },
        };
      }

      return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
    },

    onActivate: async (context: AgentContext) => {
      const isCrisis = context.transitionData.isCrisis === true;
      const agencies = context.transitionData.recommendedAgencies || [];
      if (isCrisis) {
        console.log('%c[ImageUploadAgent] Activated in CRISIS mode — all agencies will be auto-flagged after image step', 'color: #ef4444; font-weight: bold');
      } else {
        console.log('[ImageUploadAgent] Activated | agencies to report:', agencies);
      }
    },
  };
}

export default createImageUploadAgent;
